import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Layout from '@99designs/apps/spa/common/Layout/Layout';
import redirectTo from '@99designs/apps/spa/common/Ssr/redirectTo';
import { AuthConfigContextProvider, Container, PageType, Source } from '@99designs/feature-auth';
import { ErrorCodeKeys, FullscreenError, useAppConfig } from '@99designs/layout';
import { useSite } from '@99designs/spa-common';
import { Loading } from '@99designs/spa-common';
import { HttpError } from './HttpError';
import PageLayout from './PageLayout';
import fetchPreAuthorisation from './fetchPreAuthorisation';

export interface PageContainerProps {
    initialPage: PageType;
}
type PageContainerParams = {
    variant?: 'designer' | 'invite';
};

function PageContainer({ initialPage }: PageContainerProps) {
    const { recaptchaSiteKey } = useAppConfig();
    const { variant } = useParams<PageContainerParams>();
    const site = useSite();
    const navigate = useNavigate();
    const { search } = useLocation();
    const [loading, setLoading] = useState<boolean>(
        () => initialPage === 'login' || initialPage === 'signup'
    );
    const [showRecaptcha, setShowRecaptcha] = useState(true);
    const [source, setSource] = useState(() => {
        const p = new URLSearchParams(search);

        return (p.get('source') as Source) ?? Source.LOGIN_PAGE;
    });
    const [preAuthError, setPreAuthError] = useState<Error | null>(null);
    const [page, setPage] = useState(initialPage);
    const [email, setEmail] = useState('');
    const [{ clientError, recaptchaSkipToken, resetSuccessReason, target }] = useState(() => {
        const p = new URLSearchParams(search);

        const clientError = p.get('error') ?? undefined;
        const recaptchaSkipToken = p.get('recaptcha-skip-token') ?? undefined;
        const resetSuccessReason = p.get('reason') ?? undefined;
        const target = (p.get('target') || p.get('from')) ?? undefined;

        p.delete('error');
        p.delete('reason');

        navigate(
            {
                search: p.toString(),
            },
            { replace: true }
        );

        return {
            clientError,
            recaptchaSkipToken,
            resetSuccessReason,
            target,
        };
    });

    useEffect(() => {
        if (page === 'login' || page === 'signup') {
            setLoading(true);

            fetchPreAuthorisation(search)
                .then((data) => {
                    if (data.target) {
                        redirectTo(data.target);
                        return null;
                    } else {
                        if (data.skip_recaptcha) {
                            setShowRecaptcha(false);
                        }
                        if (data.email) {
                            setEmail(data.email);
                        }
                        if (data.source) {
                            setSource(data.source);
                        }
                        if (data.masquerading) {
                            setPage('masquerading');
                        }
                    }

                    setLoading(false);
                })
                .catch((e) => {
                    setLoading(false);
                    setPreAuthError(e);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    function handleSetPage(newPage: PageType, link: string) {
        if (newPage === 'login' || newPage === 'signup') {
            setLoading(true);
        }

        setPage(newPage);

        if (link) {
            navigate(link);
        }
    }

    if (preAuthError) {
        let errorCode: ErrorCodeKeys = '500';
        if (preAuthError instanceof HttpError && preAuthError.code === '429') {
            errorCode = '429';
        }
        return (
            // eslint-disable-next-line jsx-a11y/aria-role
            <Layout role="logged-out" site={site} footerStyle="reduced">
                <FullscreenError error={preAuthError} errorCode={errorCode} />
            </Layout>
        );
    }

    if (loading) {
        return <Loading />;
    }

    return (
        <AuthConfigContextProvider
            value={{
                locale: site.siteConfig.locale,
                modal: false,
                recaptchaSiteKey,
            }}
        >
            <PageLayout>
                <Container
                    email={email}
                    clientError={clientError}
                    page={page}
                    recaptchaSkipToken={recaptchaSkipToken}
                    resetSuccessReason={resetSuccessReason}
                    setPage={handleSetPage}
                    source={source}
                    showRecaptcha={showRecaptcha}
                    target={target}
                    variant={variant}
                    onAuthSuccess={(target) => window.location.assign(target)}
                />
            </PageLayout>
        </AuthConfigContextProvider>
    );
}

export default PageContainer;
